import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import pino from 'pino';

import { overviewConstants } from '../constants/appConstants';
import UserRepository from '../repositories/userRepository';
import { UserSettingRepository, type SaveUserSettingsRequest } from '../repositories/userSettingRepository';
import { queryClient } from '../router/queryClient';
import { getSquare } from '../utils/avatarUtils';
import type { InvitePeople, InvitePeopleWithPhoneNumber, User } from '../zustandStore/slices';

import { teamKeys, userKeys } from './query-keys';
import type { VerifyUserAccessParams } from './query-keys';

const logger = pino();

export const userQuery = queryOptions({
	queryKey: userKeys.getUser,
	queryFn: UserRepository.getUser,
	select: (data: User) => {
		const userAvatarToRenderInUi = getSquare(data.avatarUrl);
		return { ...data, userAvatarToRenderInUi };
	},
});

/**
 * @deprecated The method should not be used, use userQuery instead
 */
export const getUserQuery = () => ({
	queryKey: userKeys.getUser,
	queryFn: UserRepository.getUser,
	select: (data: User) => {
		const userAvatarToRenderInUi = getSquare(data.avatarUrl);
		return { ...data, userAvatarToRenderInUi };
	},
});

/**
 * @deprecated The method should not be used, use userQuery instead
 */
export const useGetUserQuery = () => {
	return useQuery(userQuery);
};

export const getUsersByRoleId = (roleId = '') => ({
	queryKey: userKeys.getUsersByRoleId(roleId),
	queryFn: () => UserRepository.getUsersByRoleId(roleId),
});

export const useGetUsersByRoleId = (roleId = '') => {
	return useQuery(getUsersByRoleId(roleId));
};

export const userByIdQuery = (id = '') =>
	queryOptions({
		queryKey: userKeys.getUserById(id),
		queryFn: () => UserRepository.getUserById(id),
		enabled: Boolean(id),
	});

export const useGetUserByEmailQuery = (email = '', enableCheckUser = false) => {
	return useQuery({
		queryKey: userKeys.getUserByEmail(email),
		queryFn: () => UserRepository.getUserByEmail(encodeURIComponent(email)),
		enabled: Boolean(email && email.length > 0 && enableCheckUser),
	});
};

export const useSendUserInvitationQuery = () => {
	return useMutation({
		mutationFn: (user: InvitePeople | InvitePeopleWithPhoneNumber) => UserRepository.sendInvitation(user),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: [teamKeys.getTeam],
			});
			void queryClient.invalidateQueries({
				queryKey: [teamKeys.getTeamUsers],
			});
			void queryClient.invalidateQueries({
				queryKey: [userKeys.getUsersByRoleId(overviewConstants.COMPANY_ADMIN)],
			});
			void queryClient.invalidateQueries({
				queryKey: [userKeys.getUsersByRoleId(overviewConstants.TEAM_ADMIN)],
			});
		},
		onError: async (error: unknown, _variables: InvitePeople, context: unknown) => {
			logger.error(`Error in sending invitation: ${error}`);
			logger.error(`Context Error in sending invitation: ${context}`);
		},
	});
};

export const getVerifyUserAccessQuery = (props: VerifyUserAccessParams) => ({
	queryKey: [userKeys.getVerifyUserAccess(props)],
	queryFn: () => UserRepository.verifyAccess(props),
});

export const useGetVerifyUserAccess = (props: VerifyUserAccessParams) => {
	return useQuery({
		...getVerifyUserAccessQuery(props),
	});
};

export const useSaveUserDataQuery = () => {
	return useMutation({
		mutationFn: UserRepository.saveUserData,
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [userKeys.getUser],
			});
		},
	});
};

export const useSaveUserSettingQuery = () => {
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse, AxiosError, SaveUserSettingsRequest>({
		mutationFn: ({ selectedTeamID }) => {
			if (selectedTeamID) {
				return UserSettingRepository.saveUserSetting({ selectedTeamID });
			} else {
				throw new Error('selectedTeamID is required');
			}
		},
		onSettled: () => {
			return queryClient.invalidateQueries({
				queryKey: [userKeys.getUserSettings, 'selectedTeamID'],
				type: 'active',
				exact: true,
			});
		},
	});
};
type UserAvatar = {
	avatar: File;
	user: User;
};

export const useSaveUserAvatarQuery = () => {
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse<User>, AxiosError, UserAvatar>({
		mutationFn: ({ avatar, user }) => UserRepository.saveAvatar({ avatar, user }),
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: [userKeys.getUser],
			});
		},
	});
};
